// screens/MyOrdersScreen.js - Screen 7
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BottomNav } from './HomeScreen';
import { EmptyState, SyncBar } from '../components/shared';
import api from '../utils/api';

export default function MyOrdersScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadSessions(); }, []);

  const loadSessions = async () => {
    try {
      const { data } = await api.get('/api/orders/mine');
      setSessions(data);
    } catch {} finally { setLoading(false); }
  };

  const syncColors = { synced: 'var(--accent-success)', pending: 'var(--accent-warning)', failed: 'var(--accent-danger)' };

  const grouped = sessions.reduce((acc, s) => {
    const key = s.order_number;
    if (!acc[key]) acc[key] = { order_number: s.order_number, product_name: s.product_name, id: s.id, sessions: [] };
    acc[key].sessions.push(s);
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />
      <div className="page-header">
        <h2>My Orders</h2>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/new-order')}>+ New</button>
      </div>

      <div className="page-content">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <div className="spinner spinner-lg" />
          </div>
        ) : sessions.length === 0 ? (
          <EmptyState
            icon="📋"
            title="No orders yet"
            message="Your photo sessions will appear here"
            action={<button className="btn btn-primary" onClick={() => navigate('/new-order')}>Start First Session</button>}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Object.values(grouped).map(group => (
              <div key={group.order_number} className="card card-hover"
                onClick={() => navigate(`/order/${group.id}`)}
                style={{ padding: 16 }}>
                {/* Order header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: '0.9375rem', color: 'var(--accent-primary)' }}>
                      {group.order_number}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                      {group.product_name}
                    </div>
                  </div>
                  <div style={{ fontSize: '1.25rem' }}>›</div>
                </div>

                {/* Sessions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {group.sessions.map((s, i) => {
                    const allSynced = s.pending_count === 0;
                    const syncStatus = allSynced ? 'synced' : 'pending';
                    return (
                      <div key={s.session_id} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 10px', background: 'var(--bg-tertiary)',
                        borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem'
                      }}>
                        <div className="sync-dot" style={{ background: syncColors[syncStatus] }} />
                        <span style={{ color: 'var(--text-secondary)' }}>Session {s.session_number}</span>
                        <span style={{ marginLeft: 'auto', color: s.synced_steps >= 6 ? 'var(--accent-success)' : 'var(--text-muted)' }}>
                          {s.synced_steps != null ? `${s.synced_steps}/6 synced` : `${s.photo_count} photos`}
                        </span>
                        {s.pending_count > 0 && (
                          <span style={{ color: 'var(--accent-warning)', fontWeight: 600 }}>
                            ↑ {s.pending_count}
                          </span>
                        )}
                        <span className={`badge ${s.status === 'completed' ? 'badge-success' : 'badge-warning'}`} style={{ marginLeft: 4 }}>
                          {s.status === 'completed' ? '✓' : '…'}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Date */}
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 10 }}>
                  {new Date(group.sessions[0].created_at || group.sessions[0].started_at).toLocaleDateString(undefined, {
                    weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav active="my-orders" />
    </div>
  );
}
